import { afterNextRender, AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, Injector, Input, OnChanges, OnDestroy, signal, ViewChild } from '@angular/core';
import { TranslatedString } from 'models/translated-string';
import { DestroyGuard } from 'services/browser-utils';
import { LL } from 'i18n/i18n.service';
import { RepProgressBarTone } from './rep-progress-bar-tone';

// the fill labels merge when the gap between them is smaller than this, in pixels
const MERGE_GAP_PX = 4;

/**
 * A horizontal rep bar on the working-set screen (#4142): the before-set range (floor to target,
 * with a tick at last time's reps) and the after-log "Last time" / "Today" comparison rows.
 *
 * Pure display. Every value is on one scale from `min` to `max`; the page passes 0 and the rep
 * target, so the floor and last time sit at the same spots in the range and in the comparison.
 * Values outside the scale are drawn at its edge. See `design-docs/working-set-screen.md`.
 */
@Component({
  selector: 'cs-rep-progress-bar',
  templateUrl: 'cs-rep-progress-bar.html',
  host: { 'class': 'rep-progress-bar' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsRepProgressBarComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** the start of the scale */
  @Input() min = 0;
  /** the end of the scale */
  @Input() max = 0;
  /** where the fill starts, on the scale */
  @Input() fillFrom = 0;
  /** where the fill ends, on the scale */
  @Input() fillTo = 0;
  /** where the tick sits, on the scale; no tick when not set */
  @Input() markerAt?: number;
  /** text shown above the tick, e.g. "last · 7" */
  @Input() markerLabel?: TranslatedString;
  /** when true, the fill's start and end values are printed under the bar */
  @Input() showFillLabels = false;
  /** how the fill is coloured: grey, or green for progress */
  @Input() tone = RepProgressBarTone.Neutral;
  /** where a round dot sits, on the scale: today's reps in a logged set's details (#4142 review fixes); no dot when not set */
  @Input() dotAt?: number;
  /** the dot's colour: green (Progress) when the set reached the target range, grey otherwise */
  @Input() dotTone = RepProgressBarTone.Neutral;
  /**
   * when true, the two fill labels become ONE "<from>–<to>" label under the end of the fill while
   * they would touch (a narrow range like 15–16), and split again when the bar is wide enough
   * (#4142 review fixes). Off, the bar draws exactly as before.
   */
  @Input() mergeFillLabels = false;
  /**
   * draw the bar like `cs-slider`: its track colour and height, a slider-thumb dot filled in its
   * tone, and a grey tick instead of a white one (owner, #4142 review fixes STOP 1). Off, the bar
   * draws exactly as before
   */
  @Input() @HostBinding('class.slider-look') sliderLook = false;

  /** exposed so the template can compare against the enum */
  readonly RepProgressBarTone = RepProgressBarTone;
  /** true while the two fill labels are shown as one */
  readonly merged = signal(false);

  @ViewChild('fromLabel') private fromLabel?: ElementRef<HTMLElement>;
  @ViewChild('toLabel') private toLabel?: ElementRef<HTMLElement>;
  private destroyGuard = new DestroyGuard();
  private resizeObserver: ResizeObserver | null = null;

  constructor(private host: ElementRef<HTMLElement>, private injector: Injector) {}

  ngOnChanges() {
    this.scheduleMergeCheck();
  }

  ngAfterViewInit() {
    // if the labels may merge, recheck whenever the bar's width changes
    if (this.mergeFillLabels && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateMerged());
      this.resizeObserver.observe(this.host.nativeElement);
    }
    this.updateMerged();
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /** the merged label's text, written like the Reps field's placeholder, e.g. "15–16" */
  get mergedLabelText(): TranslatedString {
    return LL.workouts.detail.setProgress.repsRangePlaceholder({ min: this.fillFrom, max: this.fillTo });
  }

  /** a value's position along the bar as a percentage, clamped to the bar */
  percentOf(value: number): number {
    if (!(this.max > this.min)) {
      return 0;
    }
    const percent = ((value - this.min) / (this.max - this.min)) * 100;
    return Math.min(100, Math.max(0, percent));
  }

  /** where the fill starts, as a percentage of the bar */
  get fillLeft(): number {
    return this.percentOf(this.fillFrom);
  }

  /** how long the fill is, as a percentage of the bar; never negative */
  get fillWidth(): number {
    return Math.max(0, this.percentOf(this.fillTo) - this.fillLeft);
  }

  /** true when a usable tick position was passed */
  get hasMarker(): boolean {
    return typeof this.markerAt === 'number' && Number.isFinite(this.markerAt);
  }

  /** true when a usable dot position was passed */
  get hasDot(): boolean {
    return typeof this.dotAt === 'number' && Number.isFinite(this.dotAt);
  }

  // the labels' text and positions change with the inputs, so measure again once they are drawn
  private scheduleMergeCheck() {
    if (!this.mergeFillLabels || this.destroyGuard.isDestroyed) {
      return;
    }
    afterNextRender(() => this.updateMerged(), { injector: this.injector });
  }

  // both single labels stay in the DOM (hidden while merged), so measuring them is stable and a
  // wider bar can split them back
  private updateMerged() {
    if (this.destroyGuard.isDestroyed) {
      return;
    }
    const from = this.fromLabel?.nativeElement;
    const to = this.toLabel?.nativeElement;
    if (!this.mergeFillLabels || !from || !to) {
      this.merged.set(false);
      return;
    }
    const gap = to.getBoundingClientRect().left - from.getBoundingClientRect().right;
    this.merged.set(gap < MERGE_GAP_PX);
  }
}
